import { useState } from "react";
import { useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";
import { useLocalizedNavigate } from "lib/i18n/navigation";

import { isJobActive } from "./provider-site";
import { ReadyDesignThumb } from "./ready-design-thumb";
import {
  getCurrentReadyDesign,
  getDesignGroupLabel,
  needsDraftReplaceConfirmation,
} from "./ready-designs";
import { SiteConfirmDialog } from "./site-confirm-dialog";
import { SiteHeader } from "./site-header";
import { SiteLayout, SiteLoadingCard, SiteMessageCard } from "./site-layout";
import {
  useApplyReadyDesign,
  useMyProviderSite,
  useReadyDesigns,
  useTryReadyDesign,
} from "./site.hooks";
import { notifyError, notifySuccess } from "./site.notify";
import { READY_DESIGNS_ROUTE, SITE_ROUTE } from "./site.routes";
import { openInNewTab } from "./site.web";
import { thumbFor, useThumbView } from "./thumb-view";
import { ThumbViewToggle } from "./thumb-view-toggle";
import { useSiteTranslations } from "./use-site-translations";

function ReadyDesignDetails({ profileUuid, id }: { profileUuid: string; id: string }) {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const { data, loading } = useReadyDesigns(profileUuid);
  const { data: siteData } = useMyProviderSite(profileUuid);
  const [tryDesign, { loading: isTrying }] = useTryReadyDesign();
  const [applyDesign, { loading: isApplying }] = useApplyReadyDesign();
  const [confirming, setConfirming] = useState(false);
  const [view, setView] = useThumbView();
  const site = siteData?.myProviderSite ?? null;
  const design = data?.readyDesigns.designs.find((item) => item.id === id) ?? null;
  const isCurrent = !!design && design.id === getCurrentReadyDesign(site);

  async function onTry() {
    if (!design) return;
    try {
      await openInNewTab(async () => {
        const { data: tried } = await tryDesign({ variables: { profileUuid, id: design.id } });
        if (!tried) throw new Error("No preview");
        return tried.providerSiteTryReadyDesign.previewUrl;
      });
    } catch {
      notifyError(t.designs.errors.previewFailedTitle, t.errors.tryAgain);
    }
  }

  async function apply() {
    if (!design) return;
    try {
      await applyDesign({ variables: { profileUuid, id: design.id } });
      setConfirming(false);
      notifySuccess(
        interpolateTemplate(t.designs.applied, { name: design.name }),
        t.versions.restoredMessage,
        t.title
      );
      navigate(SITE_ROUTE);
    } catch {
      setConfirming(false);
      notifyError(t.designs.errors.applyFailedTitle, t.errors.tryAgain);
    }
  }

  function onApply() {
    if (needsDraftReplaceConfirmation(site)) {
      setConfirming(true);
    } else {
      void apply();
    }
  }

  if (!design) {
    return loading ? (
      <SiteLoadingCard />
    ) : (
      <SiteMessageCard>{t.designs.loadFailed}</SiteMessageCard>
    );
  }

  const thumb = thumbFor(design, view);

  return (
    <Grid container spacing={3} data-testid="ready-design-screen">
      <Grid item xs={12}>
        <SiteHeader
          title={design.name}
          description={getDesignGroupLabel(
            design.group,
            t.designs.groups as Record<string, string>,
            t.designs.universalDesign
          )}
          backTo={READY_DESIGNS_ROUTE}
          action={
            <SoftBox
              display="flex"
              flexDirection="column"
              alignItems={{ xs: "stretch", md: "flex-end" }}
              gap={1}
            >
              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1.5}
              >
                {isCurrent ? (
                  <SoftBadge
                    variant="contained"
                    color="info"
                    size="sm"
                    container
                    badgeContent={t.designs.current}
                    data-testid="ready-design-current-badge"
                  />
                ) : null}
                <SoftButton
                  variant="outlined"
                  color="info"
                  disabled={isTrying}
                  onClick={() => void onTry()}
                  data-testid="ready-design-try-button"
                >
                  {isTrying ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <Icon sx={{ mr: 1 }}>open_in_new</Icon>
                      {t.designs.tryOnMyData}
                    </>
                  )}
                </SoftButton>
                <SoftButton
                  variant="gradient"
                  color="info"
                  // Until the site loads, a draft it may have could be replaced without the question.
                  disabled={!siteData || isApplying || isJobActive(site?.activeJob)}
                  onClick={onApply}
                  data-testid="ready-design-apply-button"
                >
                  {isApplying && !confirming ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    t.designs.apply
                  )}
                </SoftButton>
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {t.designs.applyHint}
              </SoftTypography>
            </SoftBox>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <SoftBox p={2} display="flex" flexDirection="column" alignItems="center" gap={2}>
            <SoftBox alignSelf="flex-start">
              <ThumbViewToggle view={view} onChange={setView} />
            </SoftBox>
            <SoftBox width="100%" maxWidth={thumb.view === "desktop" ? "100%" : "22rem"}>
              <ReadyDesignThumb
                uri={thumb.uri}
                desktop={thumb.view === "desktop"}
                current={isCurrent}
              />
            </SoftBox>
          </SoftBox>
        </Card>
      </Grid>
      <SiteConfirmDialog
        open={confirming}
        title={t.designs.replace.title}
        message={interpolateTemplate(t.designs.replace.message, { name: design.name })}
        confirmLabel={t.designs.replace.confirm}
        cancelLabel={t.cancel}
        busy={isApplying}
        onConfirm={() => void apply()}
        onCancel={() => setConfirming(false)}
        testId="ready-design-replace-dialog"
      >
        <SoftBox
          display="flex"
          gap={1.5}
          alignItems="flex-start"
          mt={2}
          p={1.5}
          borderRadius="lg"
          bgColor="grey-100"
        >
          <Icon color="info">history</Icon>
          <SoftTypography variant="caption">{t.designs.replace.versionsKept}</SoftTypography>
        </SoftBox>
      </SiteConfirmDialog>
    </Grid>
  );
}

// One Ready design: its thumbnail, a preview on the Provider's own data, and "Застосувати".
export function ReadyDesignPage() {
  const { id = "" } = useParams<{ id: string }>();
  return (
    <SiteLayout>
      {({ profileUuid }) => <ReadyDesignDetails profileUuid={profileUuid} id={id} />}
    </SiteLayout>
  );
}
